import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Layout, Spin } from 'antd';

import Modal from '/ui/Modal';

import filesActions from '../../actions';

import CabinetHeader from '../../../../components/Header';
import { Helmet } from '../../../../components/Helmet';
import TableEmpty from '../../../../components/TableEmpty';

import { getCookie, setCookie } from '../../../../helpers/cookie';

import { getTerminalIframeUrl, normalizeTerminalHost } from '../../utils/atlas';
import { fetchFileByUrl, getFileDownloadUrl } from '../../utils/download';
import { mergeCsvFiles } from '../../utils/mergeCsv';
import { NEURO_GENERATION_POLL_INTERVAL_MS } from '../../utils/neuroGeneration';

import { useVisible } from '../../../../hooks';
import FILES from '../../actionTypes';
import FilesGrid from './FilesGrid';
import FilesSelectionBar from './FilesSelectionBar';
import FilesToolbar from './FilesToolbar';
import GenerateAiModal from './GenerateAiModal';
import GenerateAtlasModal from './GenerateAtlasModal';
import GenerateNeuroModal from './GenerateNeuroModal';
import UploadFileModal from './UploadFileModal';

import { AppDispatch, ReduxState } from '../../../../types/app';
import { AiFileItem, AiFileStatus, FilesViewMode } from '../../types';

const { Content } = Layout;

const FILES_VIEW_MODE_COOKIE = 'filesViewMode';
const DEFAULT_VIEW_MODE: FilesViewMode = 'grid';

const isFilesViewMode = (value?: string | null): value is FilesViewMode =>
  value === 'grid' || value === 'list';

const getStoredViewMode = (): FilesViewMode => {
  const stored = getCookie(FILES_VIEW_MODE_COOKIE);

  return isFilesViewMode(stored) ? stored : DEFAULT_VIEW_MODE;
};

const FilesPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch<AppDispatch>();
  const filesState = useSelector((state: ReduxState) => state.Files);
  const [viewMode, setViewMode] = useState<FilesViewMode>(getStoredViewMode);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [generatingNeuro, setGeneratingNeuro] = useState(false);
  const [atlasModalVisible, setAtlasModalVisible] = useState(false);
  const [atlasIframeUrl, setAtlasIframeUrl] = useState<string | null>(null);
  const uploadModal = useVisible();
  const generateAtlasModal = useVisible();
  const generateAiModal = useVisible();
  const generateNeuroModal = useVisible();

  const brokerId = useSelector((state: ReduxState) => state.Data.broker?.Id);
  const clientId = useSelector((state: ReduxState) => state.Client.data?.Id);

  const { rows, readyStatus } = filesState;
  const terminalHost = useSelector(
    (state: ReduxState) => state.Data.config?.terminalHost
  );
  const themeType = useSelector(
    (state: ReduxState) => state.Data.config?.themeType
  );

  const isLoading =
    readyStatus === FILES.LOAD.INVALID || readyStatus === FILES.LOAD.REQUESTING;
  const hasFiles = rows.length > 0;
  const hasTerminalHost = useMemo(
    () => Boolean(normalizeTerminalHost(terminalHost)),
    [terminalHost]
  );
  const hasNeuroInProgress = useMemo(
    () =>
      rows.some(
        (file) =>
          file.Status === AiFileStatus.Pending ||
          file.Status === AiFileStatus.Processing
      ),
    [rows]
  );

  useEffect(() => {
    void dispatch(filesActions.Files.loadIfNeeded());
  }, [dispatch]);

  useEffect(() => {
    if (!hasNeuroInProgress) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void dispatch(filesActions.Files.loadFilesSilent());
    }, NEURO_GENERATION_POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [dispatch, hasNeuroInProgress]);

  const handleSelectChange = useCallback((id: number, checked: boolean) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }

      return prev.filter((selectedId) => selectedId !== id);
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedIds([]);
  }, []);

  const handleViewModeChange = useCallback((mode: FilesViewMode) => {
    setViewMode(mode);
    setCookie(FILES_VIEW_MODE_COOKIE, mode);
  }, []);

  const reloadFiles = useCallback(() => {
    void dispatch(filesActions.Files.loadAllFiles());
  }, [dispatch]);

  const handleGenerateNeuro = useCallback(() => {
    generateNeuroModal.show();
  }, [generateNeuroModal]);

  const handleNeuroGenerated = useCallback(
    async (fileName: string) => {
      const selectedFiles = rows.filter((file) =>
        selectedIds.includes(file.Id)
      );

      if (selectedFiles.length === 0) {
        throw new Error('No draft files selected');
      }

      setGeneratingNeuro(true);

      try {
        const fileBlobs = await Promise.all(
          selectedFiles.map(async (file) => {
            const downloadUrl = getFileDownloadUrl(
              brokerId,
              clientId,
              file.Key,
              'csv'
            );

            if (!downloadUrl) {
              throw new Error(
                `Download URL is not available for file ${file.Id}`
              );
            }

            return fetchFileByUrl(downloadUrl, { emptyOn404: true });
          })
        );

        const mergedBlob = await mergeCsvFiles(fileBlobs);
        const mergedFile = new File([mergedBlob], `${fileName}.csv`, {
          type: 'text/csv',
        });

        await dispatch(filesActions.Files.addFile(fileName, mergedFile));
        setSelectedIds([]);
      } finally {
        setGeneratingNeuro(false);
      }
    },
    [brokerId, clientId, dispatch, rows, selectedIds]
  );

  const hasSelection = selectedIds.length > 0;

  const handleAtlasFileGenerated = useCallback(
    (file: AiFileItem) => {
      const iframeUrl = getTerminalIframeUrl(
        terminalHost,
        file.Id,
        intl.locale,
        themeType,
        { appPath: true, mode: 'mode-neural-generator' }
      );

      if (!iframeUrl) return;

      setAtlasIframeUrl(iframeUrl);
      setAtlasModalVisible(true);
    },
    [intl.locale, terminalHost, themeType]
  );

  return (
    <Layout
      className={`files-page${hasSelection ? ' files-page_selection-active' : ''}`}
    >
      <Helmet title={intl.formatMessage({ id: 'files.title' })} />
      <CabinetHeader />
      <Content
        className="files-page__content"
        aria-label={intl.formatMessage({ id: 'files.page.ariaLabel' })}
      >
        <FilesToolbar
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onUploadClick={uploadModal.show}
          onGenerateAtlasClick={generateAtlasModal.show}
          onCreateAiClick={generateAiModal.show}
          isGenerateAtlasDisabled={!hasTerminalHost}
        />
        {isLoading && (
          <div className="files-page__loader">
            <Spin size="large" />
          </div>
        )}
        {!isLoading && !hasFiles && <TableEmpty />}
        {!isLoading && hasFiles && (
          <FilesGrid
            files={rows}
            viewMode={viewMode}
            selectedIds={selectedIds}
            onSelectChange={handleSelectChange}
            onAtlasIframeClose={reloadFiles}
          />
        )}
      </Content>
      <FilesSelectionBar
        visible={hasSelection}
        generating={generatingNeuro}
        generateDisabled={selectedIds.length < 2}
        onGenerate={() => handleGenerateNeuro()}
        onCancel={handleClearSelection}
      />
      {uploadModal.visible && (
        <UploadFileModal
          visible={uploadModal.visible}
          onClose={uploadModal.hide}
        />
      )}
      {generateAtlasModal.visible && (
        <GenerateAtlasModal
          visible={generateAtlasModal.visible}
          onClose={generateAtlasModal.hide}
          onGenerated={handleAtlasFileGenerated}
        />
      )}
      {generateAiModal.visible && (
        <GenerateAiModal
          visible={generateAiModal.visible}
          onClose={generateAiModal.hide}
        />
      )}
      {generateNeuroModal.visible && (
        <GenerateNeuroModal
          visible={generateNeuroModal.visible}
          onClose={generateNeuroModal.hide}
          onGenerate={handleNeuroGenerated}
        />
      )}
      <Modal
        className="files-page__atlas-modal"
        open={atlasModalVisible}
        onCancel={() => {
          setAtlasModalVisible(false);
          reloadFiles();
        }}
        footer={null}
        centered
        destroyOnHidden
        width="100%"
      >
        <iframe
          className="files-page__atlas-iframe autoHeight-iframe"
          src={atlasIframeUrl || undefined}
          title={intl.formatMessage({ id: 'files.card.changeAtlas' })}
        />
      </Modal>
    </Layout>
  );
};

export default FilesPage;
